import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { InstallButton } from '@/context/InstallContext';

export default function Login() {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      const userData = response.data.user;
      login(response.data.token, userData);
      toast.success('Welcome back!');
      
      // Use setTimeout to ensure state is updated before navigation
      setTimeout(() => {
        if (userData.role === 'admin') {
          navigate('/admin');
        } else if (userData.role === 'secretary') {
          navigate('/secretary');
        } else if (userData.role === 'technician') {
          navigate('/technician');
        } else if (userData.role === 'marketer') {
          navigate('/marketer');
        } else {
          navigate('/dashboard');
        }
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Top bar with Install Button */}
      <div className="bg-primary text-white py-2 px-4">
        <div className="max-w-md mx-auto flex justify-between items-center">
          <span className="text-sm font-medium">Excel Era Auto Care</span>
          <InstallButton variant="header" size="sm" />
        </div>
      </div>
      
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white border-2 border-border rounded-sm shadow-hard p-8">
            <div className="text-center mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_vehicle-maintenance-21/artifacts/76wdsgvy_WhatsApp%20Image%202025-12-16%20at%2017.28.15.jpeg" 
                alt="Excel Era Auto Care" 
                className="h-16 w-16 object-contain mx-auto mb-4"
              />
              <h1 className="text-3xl font-heading font-bold text-foreground">Welcome Back</h1>
              <p className="text-muted-foreground font-medium mt-2">Sign in to your account</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="email" className="font-bold uppercase text-sm tracking-wide">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  data-testid="login-email-input"
                  className="input-field mt-2"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="password" className="font-bold uppercase text-sm tracking-wide">Password</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  data-testid="login-password-input"
                  className="input-field mt-2"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="btn-primary w-full h-12 text-lg"
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-muted-foreground font-medium">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary font-bold hover:underline" data-testid="login-register-link">
                Register here
              </Link>
            </p>
          </div>

          <div className="mt-4 text-center">
            <Link to="/" className="text-sm text-muted-foreground hover:text-foreground font-medium" data-testid="login-back-link">
              ← Back to home
            </Link>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}